import { useEffect, useState } from "react";
import { Check } from "lucide-react";

// Fixed shell — order, styling, and "Most Chosen" badge stay constant.
const TIERS = [
  { id: "essentials", name: "Essentials", cta: "Choose Essentials", highlight: false },
  { id: "signature", name: "Signature", cta: "Choose Signature", highlight: true },
  { id: "bespoke", name: "Bespoke", cta: "Discuss Bespoke", highlight: false },
];

// Content by stage & tier.
const DATA = {
  existing: {
    essentials: {
      tagline: "The foundation. For a property already earning.",
      price: "$7,500",
      terms: "one-time · 4–6 week engagement",
      bullets: [
        "Discovery, strategy & brand direction — sharpened positioning built on your property's existing story and guest data",
        "Brand naming & wordmark",
        "Primary + secondary logo system",
        "Colour palette & typography",
        "Interior refresh audit — what's holding back your current photos and reviews, and what to change first",
        "1 photography direction session (re-shoot planned around your booking calendar)",
        "In-property collateral kit — housebook, branded notepads, pens & personalised thank-you cards",
      ],
      idealFor:
        "Owners with one underperforming or under-branded property, ready to reposition without a full renovation.",
    },
    signature: {
      tagline: "The full brand, repositioned with art & décor direction.",
      price: "$15,000",
      terms: "one-time · 6–8 week engagement",
      bullets: [
        "Everything in Essentials (including your interior refresh audit)",
        "Custom property art & décor accents",
        "Commissioned mural or statement wall art, creative-directed with a partner muralist — available as an add-on",
        "One-page brand website (custom domain)",
        "Direct booking channel setup — migrate demand off OTAs",
        "Instagram relaunch kit + first 12 posts",
        "Luxury in-property amenity kit — robes, slippers & welcome items",
        "Full photography, videography & drone production (2-day shoot, scheduled around bookings)",
      ],
      idealFor:
        "Owners with a property that's earning but underpriced, ready to command a premium with art-directed finishing touches and reduced platform dependency.",
    },
    bespoke: {
      tagline: "A studio partnership to reposition your portfolio.",
      price: "From $25,000",
      terms: "per property · quarterly retainer · custom scope",
      bullets: [
        "Everything in Signature, per property",
        "Master brand architecture across your existing portfolio",
        "Bespoke website with integrated booking engine",
        "Quarterly content refresh — seasonal photography, videography & drone updates across your portfolio",
        "Commissioned mural available per property, from $5,500/property when 3+ properties are commissioned together",
        "Ongoing performance reporting — ADR & occupancy, pre- and post-reposition",
      ],
      idealFor:
        "Boutique collections and operators with multiple existing properties ready to reposition as one branded portfolio, with ongoing content production at scale.",
    },
  },
  new: {
    essentials: {
      tagline: "The foundation. Ready before you open.",
      price: "$8,500",
      terms: "one-time · 6-week engagement",
      bullets: [
        "Discovery, strategy & brand direction — defining your story and positioning from a blank slate",
        "Brand naming & wordmark",
        "Primary + secondary logo system",
        "Colour palette & typography",
        "Interior design direction session — style brief for your architect or contractor",
        "1 photography direction session (pre-launch shoot planning)",
        "In-property collateral kit — housebook, branded notepads, pens & personalised thank-you cards",
      ],
      idealFor:
        "Owners developing one flagship property, launching with a defined identity from day one.",
    },
    signature: {
      tagline: "The full brand, elevated with art & décor direction.",
      price: "$20,000",
      terms: "one-time · 8–10 week engagement",
      bullets: [
        "Everything in Essentials (including your interior design direction session)",
        "Custom property art & décor curation",
        "Commissioned mural or statement wall art, creative-directed with a partner muralist — available as an add-on",
        "One-page brand website (custom domain)",
        "Direct booking channel setup",
        "Instagram launch kit + first 12 posts",
        "Luxury in-property amenity kit — robes, slippers & welcome items",
        "Full photography, videography & drone production (2-day shoot at launch)",
      ],
      idealFor:
        "Developers and owners launching a flagship property, ready to open with a premium identity, art-directed finishing touches, and direct booking demand from day one.",
    },
    bespoke: {
      tagline: "A studio partnership for a portfolio in development.",
      price: "From $35,000",
      terms: "per property · quarterly retainer · custom scope",
      bullets: [
        "Everything in Signature, per property",
        "Master brand architecture (parent + sub-brands) across your portfolio",
        "Bespoke website with integrated booking engine",
        "Quarterly content refresh — new photography, videography & drone footage as each property comes online",
        "Commissioned mural available per property, from $5,500/property when 3+ properties are commissioned together",
        "Ongoing brand consulting as new properties come online",
      ],
      idealFor:
        "Developers bringing multiple new properties to market who need one cohesive brand system and ongoing content production across the portfolio.",
    },
  },
};

const STAGES = [
  { id: "new", label: "Launching a New Property" },
  { id: "existing", label: "Repositioning an Existing Property" },
];

const readInitialStage = () => {
  if (typeof window === "undefined") return "existing";
  const params = new URLSearchParams(window.location.search);
  const q = params.get("stage");
  if (q === "new" || q === "existing") return q;
  return "existing";
};

export default function Packages() {
  const [stage, setStage] = useState(readInitialStage);
  const [contentKey, setContentKey] = useState(0);

  // Bump key on change to trigger the fade-in transition.
  useEffect(() => {
    setContentKey((k) => k + 1);
  }, [stage]);

  // Keep URL in sync (shareable + preserved on refresh) without scroll jump.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("stage") !== stage) {
      params.set("stage", stage);
      const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
      window.history.replaceState(null, "", newUrl);
    }
  }, [stage]);

  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#F2EEE6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-6 reveal">
            <p className="overline mb-6">The Offering</p>
            <h2 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.02em] text-[#1A1A1A] font-light">
              Three ways
              <br />
              <span className="italic text-[#6B5C4D]">to become</span>
              <br />a destination.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 reveal flex md:justify-end">
            <p className="text-[#5C564D] text-lg leading-relaxed max-w-md">
              Transparent, fixed-scope engagements. Every package is delivered
              in-studio by a designer with a decade of brand work across
              fashion, food and hospitality — now applied to the very
              specific problem of making a short-stay property earn more.
            </p>
          </div>
        </div>

        {/* Stage toggle */}
        <div
          role="tablist"
          aria-label="Property stage"
          data-testid="stage-toggle"
          className="reveal mx-auto mb-12 md:mb-16 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2 sm:gap-0 sm:inline-flex sm:mx-auto w-full sm:w-auto p-1 bg-white border border-[#1A1A1A]/10 rounded-full"
        >
          {STAGES.map((s) => {
            const active = stage === s.id;
            return (
              <button
                key={s.id}
                role="tab"
                aria-selected={active}
                data-testid={`stage-option-${s.id}`}
                onClick={() => setStage(s.id)}
                className={`w-full sm:w-auto h-12 sm:h-11 px-6 md:px-8 rounded-full text-[11px] md:text-[12px] tracking-[0.22em] uppercase font-medium transition-all duration-500 whitespace-nowrap ${
                  active
                    ? "bg-[#1A1A1A] text-[#FAF9F6] shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                    : "text-[#1A1A1A]/70 hover:text-[#1A1A1A]"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {TIERS.map((t, i) => {
            const content = DATA[stage][t.id];
            return (
              <article
                key={t.id}
                data-testid={`package-card-${t.id}`}
                className={`reveal border p-8 md:p-10 flex flex-col transition-all duration-500 hover:-translate-y-1 ${
                  t.highlight
                    ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FAF9F6]"
                    : "border-[#1A1A1A]/12 bg-white text-[#1A1A1A] hover:border-[#1A1A1A]/40"
                }`}
              >
                <div className="flex items-baseline justify-between mb-8">
                  <p className={`overline ${t.highlight ? "text-[#A89680]" : ""}`}>
                    Package {String(i + 1).padStart(2, "0")}
                  </p>
                  {t.highlight && (
                    <span
                      data-testid="package-badge-most-chosen"
                      className="text-[10px] tracking-[0.22em] uppercase text-[#A89680]"
                    >
                      Most Chosen
                    </span>
                  )}
                </div>

                <h3 className="font-serif-editorial text-4xl md:text-5xl leading-none mb-4">
                  {t.name}
                </h3>

                {/* Fading content that swaps with the stage */}
                <div
                  key={`${t.id}-${contentKey}`}
                  data-testid={`package-content-${t.id}`}
                  className="flex flex-col flex-1 animate-[mlFadeIn_500ms_ease-out]"
                >
                  <p
                    className={`text-sm md:text-base leading-relaxed mb-8 ${
                      t.highlight ? "text-[#FAF9F6]/70" : "text-[#5C564D]"
                    }`}
                    data-testid={`package-tagline-${t.id}`}
                  >
                    {content.tagline}
                  </p>

                  <div className="mb-10">
                    <p
                      className="font-serif-editorial text-3xl md:text-4xl"
                      data-testid={`package-price-${t.id}`}
                    >
                      {content.price}
                    </p>
                    <p
                      className="text-xs uppercase tracking-[0.2em] mt-2 text-[#A89680]"
                      data-testid={`package-terms-${t.id}`}
                    >
                      {content.terms}
                    </p>
                  </div>

                  <ul
                    className="space-y-3 mb-10 flex-1"
                    data-testid={`package-bullets-${t.id}`}
                  >
                    {content.bullets.map((f, fi) => (
                      <li
                        key={fi}
                        className="flex items-start gap-3 text-sm md:text-[15px] leading-relaxed"
                      >
                        <Check
                          className={`w-4 h-4 mt-1 shrink-0 ${
                            t.highlight ? "text-[#A89680]" : "text-[#6B5C4D]"
                          }`}
                          strokeWidth={1.5}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div
                    className={`pt-6 border-t ${
                      t.highlight ? "border-white/15" : "border-black/10"
                    } mb-8`}
                  >
                    <p className="text-xs uppercase tracking-[0.22em] mb-2 text-[#A89680]">
                      Ideal for
                    </p>
                    <p
                      className={`text-sm leading-relaxed ${
                        t.highlight ? "text-[#FAF9F6]/85" : "text-[#5C564D]"
                      }`}
                      data-testid={`package-ideal-${t.id}`}
                    >
                      {content.idealFor}
                    </p>
                  </div>
                </div>

                {/* CTA — unchanged across stages */}
                <a
                  href="#contact"
                  data-testid={`package-cta-${t.id}`}
                  onClick={() => {
                    try {
                      sessionStorage.setItem("ml_pkg", t.name);
                    } catch (_) {
                      /* ignore storage errors */
                    }
                  }}
                  className={`inline-flex items-center justify-center h-13 py-4 px-6 text-[11px] tracking-[0.24em] uppercase font-medium transition-colors duration-500 ${
                    t.highlight
                      ? "bg-[#FAF9F6] text-[#1A1A1A] hover:bg-[#A89680] hover:text-white"
                      : "bg-[#1A1A1A] text-[#FAF9F6] hover:bg-[#A89680]"
                  }`}
                >
                  {t.cta}
                </a>
              </article>
            );
          })}
        </div>

        <p className="mt-16 text-center text-sm text-[#5C564D] reveal">
          Not sure which tier? &nbsp;
          <a
            href="#contact"
            data-testid="packages-help-link"
            className="hover-underline text-[#1A1A1A] font-medium"
          >
            Request a personal recommendation →
          </a>
        </p>
      </div>

      <style>{`
        @keyframes mlFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
